import { useCallback, useState } from 'react';
import type { Team } from '~/types/team';

export interface TeamMentionState {
  isOpen: boolean;
  query: string;
  position: number;
  triggerPosition: number;
}

interface UseTeamMentionsProps {
  onTeamMentionSelect: (team: Team) => void;
  teams: Team[];
}

export const useTeamMentions = ({
  onTeamMentionSelect,
  teams,
}: UseTeamMentionsProps) => {
  const [mentionState, setMentionState] = useState<TeamMentionState>({
    isOpen: false,
    query: '',
    position: 0,
    triggerPosition: 0,
  });

  const filteredTeams = teams.filter(
    (team) =>
      team.name.toLowerCase().includes(mentionState.query.toLowerCase()) ||
      team.type.toLowerCase().includes(mentionState.query.toLowerCase()),
  );

  const handleInputChange = useCallback(
    (value: string, selectionStart: number) => {
      const beforeCursor = value.substring(0, selectionStart);
      const hashIndex = beforeCursor.lastIndexOf('#');

      if (hashIndex === -1) {
        setMentionState((prev) => ({ ...prev, isOpen: false }));
        return;
      }

      const afterHash = beforeCursor.substring(hashIndex + 1);
      const hasSpaceAfterHash = afterHash.includes(' ');

      if (hasSpaceAfterHash) {
        setMentionState((prev) => ({ ...prev, isOpen: false }));
        return;
      }

      setMentionState({
        isOpen: true,
        query: afterHash,
        position: selectionStart,
        triggerPosition: hashIndex,
      });
    },
    [],
  );

  const handleTeamMentionSelect = useCallback(
    (team: Team) => {
      onTeamMentionSelect(team);
      setMentionState((prev) => ({ ...prev, isOpen: false }));
    },
    [onTeamMentionSelect],
  );

  const closeTeamMentions = useCallback(() => {
    setMentionState((prev) => ({ ...prev, isOpen: false }));
  }, []);

  return {
    mentionState,
    filteredTeams,
    handleInputChange,
    handleTeamMentionSelect,
    closeTeamMentions,
  };
};
